// Minimal shape the Compliance Documents sort needs: which question a row
// answers, and when it was uploaded. Kept structural so both file and adoption
// UploadRow variants satisfy it without importing the component's types.
type OrderableRow = { questionId: string; created_at: string };

// Rows whose question is no longer in the effective set (e.g. removed from the
// questionnaire) have no display_order — sort them after everything ordered.
const NO_ORDER = Number.MAX_SAFE_INTEGER;

/**
 * Order Compliance Documents rows to mirror the questionnaire's question order.
 *
 * The vendor's My Documents view groups uploads under their category, but within
 * a category the list should read the same way the questionnaire does — top to
 * bottom by question — rather than by whenever each file happened to be uploaded.
 * We sort by the owning question's `display_order`, falling back to `created_at`
 * as a stable tiebreaker when a single question produced several files/adoptions.
 *
 * Returns a new array; the input is not mutated.
 */
export function sortUploadRowsByQuestion<T extends OrderableRow>(
  rows: T[],
  displayOrderByQuestionId: Map<string, number>,
): T[] {
  return [...rows].sort((a, b) => {
    const orderA = displayOrderByQuestionId.get(a.questionId) ?? NO_ORDER;
    const orderB = displayOrderByQuestionId.get(b.questionId) ?? NO_ORDER;
    if (orderA !== orderB) return orderA - orderB;
    return a.created_at.localeCompare(b.created_at);
  });
}
